import { useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { db } from '../../lib/firebase';
import useAuth from '../../hooks/useAuth';
import useParameters from '@/hooks/useParameters';
import useMaintenanceActive from '@/hooks/useMaintenanceActive';

// Firestore-only reachability indicator. If Firestore is reachable, the
// rest of the Firebase suite (Auth, Storage, Functions) is overwhelmingly
// likely to be reachable too — they share infrastructure and auth tokens.
function BackendStatusIndicator() {
  const theme = useTheme();
  const { user } = useAuth();
  const { maintenanceDB } = useParameters();
  const [online, setOnline] = useState(navigator.onLine);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsub = db
      .collection('users')
      .doc(user.uid)
      .onSnapshot({ includeMetadataChanges: true }, (snap) => {
        setFromCache(snap.metadata.fromCache);
      });
    return () => unsub();
  }, [user?.uid]);

  const isConnected = online && !fromCache;
  // Ámbar durante la mantención. Solo se llega a ver donde la cortina no tapa
  // la app: en dev (exento) y con un usuario de allowedUids.
  const inMaintenance = useMaintenanceActive(maintenanceDB);
  let color = theme.palette.success.main;
  let label = 'Conectado';
  if (!isConnected) {
    color = theme.palette.error.main;
    label = !online
      ? 'Sin conexión a internet'
      : 'No se puede conectar al sistema — operando desde caché';
  } else if (inMaintenance) {
    color = theme.palette.warning.main;
    label = maintenanceDB?.message
      ? `Mantención en curso — ${maintenanceDB.message}`
      : 'Mantención en curso';
  }

  return (
    <Tooltip title={label} arrow>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: `0 0 6px ${color}`,
          transition: 'background-color 0.3s, box-shadow 0.3s',
        }}
        aria-label={label}
        role="status"
      />
    </Tooltip>
  );
}

export default BackendStatusIndicator;
